import { createBlock } from 'factories/block';
import { InteractionProps, repeat } from './interaction';
import { createLayout } from 'factories/layout';
import { createFeedbackBlock } from './feedback';
import { colors } from 'styles/variables';
import { Block, cloneBlock, Blocks } from 'factories/block';
import { flatten } from 'factories/utils';
import { createContinueBtn } from './continueBtn';
import { createEditableBtn } from './editableBtn';
import { createMediaBlock } from './media';
import { getSId } from '../utils';
import { getPlaceholderText } from './text';

const { lGrey0 } = colors;

export type MultichoiceInteractionProps = InteractionProps & {
  answerCount: number;
  isMultichoiceMedia?: boolean;
};

const createMultichoiceMediaOption = () => {
  return createBlock('MultichoiceMedia', [
    createMediaBlock({
      child: 1,
    }),
    createBlock('Text', [], {
      props: {
        content: 'Example text',
        className: 'text-align-center',
      },
    }),
  ]);
};

export const createMultichoiceBlock = (count: number, isMultichoiceMedia) => {
  return createBlock('Multichoice', [
    ...repeat(
      () =>
        createBlock('Answer', [
          isMultichoiceMedia
            ? createMultichoiceMediaOption()
            : createBlock('Text', [], {
                props: {
                  // bulletColor: '#fff',
                  content: 'Example text',
                },
              }),
          createFeedbackBlock({}),
        ]),
      count
    ),
  ]);
};

export const createMultichoiceBlockWithText = (
  count: number,
  isMultichoiceMedia,
  sharedQuestionMedia
) => {
  return createBlock('MultichoiceWithText', [
    sharedQuestionMedia,
    createEditableBtn({
      type: 'Submit_Button',
      label: 'Submit',
    }),
    createMultichoiceBlock(count, isMultichoiceMedia),

    createEditableBtn({
      type: 'ReviewFeedback',
      label: 'Review feedback',
    }),
  ]);
};

const contentForColumn = (
  count: number,
  isMultichoiceMedia,
  sharedQuestionMedia
) => (i: number) => {
  return [
    () =>
      isMultichoiceMedia
        ? createBlock('ColumnContentForMultichoiceMedia', [
            createMediaBlock({
              child: 1,
            }),
            sharedQuestionMedia,
          ])
        : createMediaBlock({
            child: 1,
          }),

    () =>
      createBlock('InteractionColumn', [
        createMultichoiceBlockWithText(
          count,
          isMultichoiceMedia,
          sharedQuestionMedia
        ),
        createMediaBlock({
          child: 0,
          isAdditionalMedia: true,
        }),
      ]),
  ][i];
};

export const removeMultichoiceOption = (
  block: Block,
  position: number,
  isMultichoiceMedia
): [Block, Blocks?] => {
  if (block.type === 'Multichoice') {
    const optionBlock = createBlock('Answer', [
      isMultichoiceMedia
        ? createMultichoiceMediaOption()
        : createBlock('Text', [], {
            props: {
              content: 'Example text content',
              className: 'text-align-center',
            },
          }),
      createFeedbackBlock({}),
    ]);
    const [newBlocks] = flatten(optionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 1);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const addMultichoiceOption = (
  block: Block,
  position: number,
  isMultichoiceMedia
): [Block, Blocks?] => {
  if (block.type === 'Multichoice') {
    const optionBlock = createBlock('Answer', [
      isMultichoiceMedia
        ? createMultichoiceMediaOption()
        : createBlock('Text', [], {
            props: {
              content: 'Example text',
            },
          }),
      createFeedbackBlock({}),
    ]);
    const optionBlockRef = { id: optionBlock.id, type: 'Ref' };
    const [newBlocks] = flatten(optionBlock);

    const nextBlock = cloneBlock(block);
    (nextBlock.children as Block[]).splice(position, 0, optionBlockRef);

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const createMultichoiceInteractionBlock = (
  props: MultichoiceInteractionProps
) => {
  const {
    textColor = '',
    checkboxColor = '#fff',
    correctOptions = '1',
    numberOfAttempts = 1,
    lockDown = 0,
    showCorrectAnswer = 1,
    useUniqueOptionFeedback = false,
    score = true,
    style,
    isMultichoiceMedia,
  } = props as any;

  const panelBackground = isMultichoiceMedia ? '#fff' : lGrey0;
  const optionTextColor = isMultichoiceMedia ? '#000' : '#fff';

  const sharedQuestionMedia = createMediaBlock({
    child: 0,
    imgPlaceholderIndex: 1,
    textPlaceholderContent: getPlaceholderText('h2', 'heading-two'),
    className: isMultichoiceMedia ? 'text-align-center' : '',
  });

  return createBlock(
    'Interaction',
    [
      createLayout(
        contentForColumn(
          props.answerCount,
          isMultichoiceMedia,
          sharedQuestionMedia
        ),
        isMultichoiceMedia ? 0 : props.columnCount,
        isMultichoiceMedia ? true : props.columnsReversed
      ),
      createFeedbackBlock({}),
      createBlock('SingleColumnLayout', [
        createBlock('Column', [createContinueBtn(style)], {
          props: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          },
          meta: {
            subType: 'ButtonBlock',
          },
        }),
      ]),
    ],
    {
      props: {
        name: props.name,
        sId: getSId('interaction'),
        style: props.style,
        textColor,
        correctOptions,
        numberOfAttempts,
        lockDown,
        showCorrectAnswer,
        useUniqueOptionFeedback,
        score,
        isMultichoiceMedia,
      },
      meta: {
        subType: 'Multichoice',
        layout: isMultichoiceMedia
          ? 'OneColumnReversed'
          : `${props.columnCount === 0 ? 'OneColumn' : 'TwoColumns'}${
              props.columnsReversed ? 'Reversed' : ''
            }`,
      },
    }
  );
};

export const create = createMultichoiceInteractionBlock;
export const interactionConfig = {};
